import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

type DateTimePickerUiProps = {
  value: dayjs.Dayjs | null;
  onChange: (date: dayjs.Dayjs | null) => void;
  disabled?: boolean;
}

const DateTimePickerUi = (props: DateTimePickerUiProps) => {
  const {
    value,
    onChange,
    disabled,
  } = props;

  // Disable times outside 9am - 8pm
  const shouldDisableTime = (value: dayjs.Dayjs, view: 'hours' | 'minutes' | 'seconds') => {
    if (view === 'hours') {
      const hour = value.hour();
      // Disable before 9am and after 8pm
      return hour < 9 || hour >= 21;
    }
    return false;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <DateTimePicker
        value={value}
        onChange={onChange}
        disabled={disabled}
        minDateTime={dayjs().add(2, 'day').startOf('day').hour(9)}
        shouldDisableTime={shouldDisableTime}
        slotProps={{
          openPickerButton: {
            sx: {
              color: 'var(--primary-color) !important',
            },
          },
          textField: {
            sx: {
              '& .MuiPickersOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'var(--primary-color) !important',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--primary-color) !important',
                },
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--primary-color) !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--primary-color) !important',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--primary-color) !important',
              },
              '& .MuiIconButton-root': {
                color: 'var(--primary-color) !important',
                '& .MuiSvgIcon-root': {
                  color: 'var(--primary-color) !important',
                  fill: 'var(--primary-color) !important',
                },
              },
              '& .MuiInputAdornment-root .MuiIconButton-root': {
                color: 'var(--primary-color) !important',
              },
              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                color: 'var(--primary-color) !important',
                fill: 'var(--primary-color) !important',
              },
            },
          },
          dialog: {
            sx: {
              // Mobile dialog styling
              '& .MuiTab-root': {
                color: 'var(--primary-color) !important',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'var(--primary-color) !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'var(--primary-color) !important',
              },
              '& .MuiPickersDay-root.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              '& .MuiPickersDay-root.Mui-selected:hover': {
                backgroundColor: '#22C55E !important',
              },
              '& .MuiYearCalendar-button.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              '& .MuiYearCalendar-button.Mui-selected:hover': {
                backgroundColor: '#22C55E !important',
              },
              '& .MuiClockNumber-root.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              '& .MuiClock-pin': {
                backgroundColor: 'var(--primary-color) !important',
              },
              '& .MuiClockPointer-root': {
                backgroundColor: 'var(--primary-color) !important',
              },
              '& .MuiClockPointer-thumb': {
                backgroundColor: 'var(--primary-color) !important',
                borderColor: 'var(--primary-color) !important',
              },
              '& .MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              '& .MuiPickersToolbar-root': {
                backgroundColor: 'var(--primary-color) !important',
              },
              '& .MuiButton-root': {
                color: 'var(--text-color) !important',
              },
              '& .MuiButton-root.MuiButton-text:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: 'var(--primary-color) !important',
              },
            },
          },
          popper: {
            placement: 'bottom-start',
            disablePortal: true,
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: 'var(--white-color)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid var(--background-color-light)',
              },
              // Tab icons for date/time switcher
              '& .MuiTab-root': {
                color: 'var(--primary-color) !important',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'var(--primary-color) !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'var(--primary-color) !important',
              },
              // Selected date
              '& .MuiPickersDay-root.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              '& .MuiPickersDay-root.Mui-selected:hover': {
                backgroundColor: '#22C55E !important',
              },
              // Selected year
              '& .MuiYearCalendar-button.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              '& .MuiYearCalendar-button.Mui-selected:hover': {
                backgroundColor: '#22C55E !important',
              },
              // Selected time number
              '& .MuiClockNumber-root.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              // Clock center pin
              '& .MuiClock-pin': {
                backgroundColor: 'var(--primary-color) !important',
              },
              // Clock pointer line
              '& .MuiClockPointer-root': {
                backgroundColor: 'var(--primary-color) !important',
              },
              // Clock pointer thumb (end circle)
              '& .MuiClockPointer-thumb': {
                backgroundColor: 'var(--primary-color) !important',
                borderColor: 'var(--primary-color) !important',
              },
              // AM/PM buttons
              '& .MuiPickersArrowSwitcher-button': {
                color: 'var(--primary-color) !important',
              },
              // Digital clock selected time
              '& .MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                backgroundColor: 'var(--primary-color) !important',
                color: 'var(--white-color) !important',
              },
              '& .MuiMultiSectionDigitalClockSection-item.Mui-selected:hover': {
                backgroundColor: '#22C55E !important',
              },
              // Buttons (Cancel, OK, etc.)
              '& .MuiButton-root': {
                color: 'var(--text-color) !important',
              },
              '& .MuiButton-root:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default DateTimePickerUi;
